import {
    FormField,
    Input,
    SegmentedControl,
    Select,
    SpaceBetween,
} from "@amzn/awsui-components-react";
import {
    OptionDefinition,
    OptionGroup,
} from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { FC } from "react";
import { BasemapSelectionType, SideConfig } from "../../state";
import { SelectOption } from "./ConfigBar";

export interface SideConfigFormProps {
    /** The draft config this form edits. */
    config: SideConfig;
    /** Source/environment dropdown options. */
    sourceOptions: SelectOption[];
    /** Stylesheet dropdown options (may be country-grouped). */
    stylesheetOptions: SelectOption[];
    /** Version dropdown options (may be status-grouped). */
    versionOptions: SelectOption[];
    /** Patch handler for any field on the config. */
    onChange: (patch: Partial<SideConfig>) => void;
}

// NOTE: a tile-format badge (MVT/PMTiles/MLT via engine `detectTileFormat`) was
// intentionally removed per UI feedback (noise for the common MVT case). If we
// revisit it, render it BESIDE the tileset Input (as a secondaryControl), not
// below — and likely only for non-MVT formats once PMTiles/MLT land (P1/P2).

/** Finds a flat option by value, recursing into option groups. */
const findOption = (
    options: SelectOption[],
    value: string | undefined
): OptionDefinition | null => {
    for (const opt of options) {
        const group = opt as OptionGroup;
        if (Array.isArray(group.options)) {
            const hit = group.options.find((o) => o.value === value);
            if (hit) return hit;
        } else if ((opt as OptionDefinition).value === value) {
            return opt as OptionDefinition;
        }
    }
    return null;
};

/**
 * Editable config for one map side: basemap selection (select vs manual),
 * source/stylesheet/version, and an optional tileset override. Presentational
 * and controlled — emits patches via onChange; the parent decides when to fetch
 * (Refresh) or apply (Update).
 */
const SideConfigForm: FC<SideConfigFormProps> = ({
    config,
    sourceOptions,
    stylesheetOptions,
    versionOptions,
    onChange,
}) => {
    return (
        <SpaceBetween direction="vertical" size="s">
            <FormField label="Type">
                <SegmentedControl
                    selectedId={config.selectionType}
                    onChange={({ detail }) =>
                        onChange({ selectionType: detail.selectedId as BasemapSelectionType })
                    }
                    options={[
                        { id: "select", text: "Select" },
                        { id: "manual", text: "Manual" },
                    ]}
                />
            </FormField>

            {config.selectionType === "select" ? (
                // Stacked vertically: in the ~360px floating panel, three
                // side-by-side selects caused label/word wrap (feedback #6).
                <SpaceBetween direction="vertical" size="s">
                    <FormField label="Source">
                        <Select
                            selectedOption={findOption(sourceOptions, config.environment)}
                            options={sourceOptions}
                            expandToViewport
                            onChange={({ detail }) =>
                                onChange({ environment: detail.selectedOption.value })
                            }
                        />
                    </FormField>
                    <FormField label="Stylesheet">
                        <Select
                            selectedOption={findOption(stylesheetOptions, config.stylesheetId)}
                            options={stylesheetOptions}
                            filteringType="auto"
                            expandToViewport
                            onChange={({ detail }) =>
                                onChange({ stylesheetId: detail.selectedOption.value })
                            }
                        />
                    </FormField>
                    <FormField label="Version">
                        <Select
                            selectedOption={findOption(versionOptions, config.stylesheetVersion)}
                            options={versionOptions}
                            expandToViewport
                            onChange={({ detail }) =>
                                onChange({ stylesheetVersion: detail.selectedOption.value })
                            }
                        />
                    </FormField>
                </SpaceBetween>
            ) : (
                <FormField label="Stylesheet URL">
                    <Input
                        value={config.manualStylesheetUrl}
                        type="url"
                        inputMode="url"
                        placeholder="https://…/style.json"
                        onChange={({ detail }) => onChange({ manualStylesheetUrl: detail.value })}
                    />
                </FormField>
            )}

            <FormField
                label="Tileset URL"
                description="Leave blank to use the stylesheet's default tiles."
            >
                <Input
                    value={config.tilesetUrl}
                    type="url"
                    inputMode="url"
                    placeholder="https://…/{z}/{x}/{y}.mvt"
                    onChange={({ detail }) => onChange({ tilesetUrl: detail.value })}
                />
            </FormField>
        </SpaceBetween>
    );
};

export default SideConfigForm;
